import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Badge,
  CircularProgress
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  VerifiedUser,
  Block,
  CheckCircle,
  Warning,
  Security,
  Assessment,
  Timeline,
  LocationOn,
  CalendarToday,
  Euro,
  Star,
  Report,
  Visibility,
  Edit,
  Delete,
  Refresh,
  FilterList,
  Search,
  Flag,
  Gavel,
  Analytics
} from '@mui/icons-material';
import VerifiedBadge from './VerifiedBadge';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  status: 'active' | 'blocked' | 'pending_verification' | 'suspended';
  verificationStatus: {
    email: boolean;
    phone: boolean;
    identity: boolean;
    address: boolean;
  };
  registrationDate: Date;
  lastActivity: Date;
  totalTransactions: number;
  totalSpent: number;
  rating: number;
  reports: number;
  isVerified: boolean;
  riskScore: number;
  suspiciousActivity: Array<{
    type: 'multiple_accounts' | 'fake_photos' | 'suspicious_payments' | 'reported_multiple_times';
    description: string;
    severity: 'low' | 'medium' | 'high';
    timestamp: Date;
  }>;
  profileCompleteness: number;
  location: string;
  age: number;
  bio: string;
  socialMedia: {
    instagram?: string;
    facebook?: string;
    linkedin?: string;
  };
}

interface UserManagementProps {
  onUserAction: (userId: string, action: string, data?: any) => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ onUserAction }) => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openVerificationDialog, setOpenVerificationDialog] = useState(false);
  const [filter, setFilter] = useState<'all' | 'high_risk' | 'pending' | 'blocked'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    
    // Simulation du chargement des utilisateurs
    setTimeout(() => {
      setUsers([
        {
          id: '1',
          name: 'Marie Dubois',
          email: 'marie.dubois@email.com',
          phone: '+33 1 23 45 67 89',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150',
          status: 'active',
          verificationStatus: { email: true, phone: true, identity: true, address: true },
          registrationDate: new Date('2024-01-15'),
          lastActivity: new Date(Date.now() - 2 * 60 * 60 * 1000),
          totalTransactions: 12,
          totalSpent: 1200,
          rating: 4.8,
          reports: 0,
          isVerified: true,
          riskScore: 15,
          suspiciousActivity: [],
          profileCompleteness: 95,
          location: 'Paris, France',
          age: 28,
          bio: 'Passionnée de mode et de robes élégantes',
          socialMedia: {
            instagram: '@marie_dubois',
            linkedin: 'marie-dubois'
          }
        },
        {
          id: '2',
          name: 'Jean Martin',
          email: 'jean.martin@email.com',
          phone: '+33 1 98 76 54 32',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150',
          status: 'blocked',
          verificationStatus: { email: true, phone: false, identity: false, address: false },
          registrationDate: new Date('2024-02-01'),
          lastActivity: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          totalTransactions: 3,
          totalSpent: 300,
          rating: 2.1,
          reports: 5,
          isVerified: false,
          riskScore: 85,
          suspiciousActivity: [
            {
              type: 'multiple_accounts',
              description: 'Plusieurs comptes créés avec des emails similaires',
              severity: 'high',
              timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
            },
            {
              type: 'reported_multiple_times',
              description: 'Signalé 5 fois par différents utilisateurs',
              severity: 'high',
              timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
            }
          ],
          profileCompleteness: 45,
          location: 'Lyon, France',
          age: 35,
          bio: '',
          socialMedia: {}
        },
        {
          id: '3',
          name: 'Sophie Laurent',
          email: 'sophie.laurent@email.com',
          phone: '+33 1 55 44 33 22',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150',
          status: 'pending_verification',
          verificationStatus: { email: true, phone: true, identity: false, address: false },
          registrationDate: new Date('2024-03-01'),
          lastActivity: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          totalTransactions: 0,
          totalSpent: 0,
          rating: 0,
          reports: 0,
          isVerified: false,
          riskScore: 45,
          suspiciousActivity: [
            {
              type: 'fake_photos',
              description: 'Photos de profil détectées comme potentiellement fausses',
              severity: 'medium',
              timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)
            }
          ],
          profileCompleteness: 60,
          location: 'Marseille, France',
          age: 25,
          bio: 'Nouvelle sur la plateforme',
          socialMedia: {
            instagram: '@sophie_l'
          }
        },
        {
          id: '4',
          name: 'Pierre Durand',
          email: 'pierre.durand@email.com',
          phone: '+33 1 77 88 99 00',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
          status: 'active',
          verificationStatus: { email: true, phone: true, identity: true, address: false },
          registrationDate: new Date('2024-01-20'),
          lastActivity: new Date(Date.now() - 30 * 60 * 1000),
          totalTransactions: 8,
          totalSpent: 800,
          rating: 4.2,
          reports: 1,
          isVerified: true,
          riskScore: 25,
          suspiciousActivity: [],
          profileCompleteness: 80,
          location: 'Toulouse, France',
          age: 32,
          bio: 'Collectionneur de robes vintage',
          socialMedia: {
            facebook: 'pierre.durand',
            linkedin: 'pierre-durand'
          }
        }
      ]);
      setLoading(false);
    }, 1500);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'blocked': return 'error';
      case 'pending_verification': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getRiskLevel = (score: number) => {
    if (score >= 70) return { level: 'Élevé', color: 'error' };
    if (score >= 40) return { level: 'Moyen', color: 'warning' };
    return { level: 'Faible', color: 'success' };
  };

  const handleUserAction = (userId: string, action: string) => {
    setUsers(prevUsers => 
      prevUsers.map(user => {
        if (user.id === userId) {
          switch (action) {
            case 'block':
              return { ...user, status: 'blocked' as const };
            case 'unblock':
              return { ...user, status: 'active' as const };
            case 'suspend':
              return { ...user, status: 'suspended' as const };
            case 'verify':
              return { 
                ...user, 
                isVerified: true, 
                verificationStatus: { ...user.verificationStatus, identity: true },
                status: 'active' as const
              };
            default:
              return user;
          }
        }
        return user;
      })
    );
    onUserAction(userId, action);
  };

  const filteredUsers = users.filter(user => {
    const matchesFilter = filter === 'all' || 
      (filter === 'high_risk' && user.riskScore >= 70) ||
      (filter === 'pending' && user.status === 'pending_verification') ||
      (filter === 'blocked' && user.status === 'blocked');
    
    const matchesSearch = searchTerm === '' || 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const getVerificationBadges = (user: UserProfile) => {
    const badges = [];
    if (user.verificationStatus.email) badges.push({ type: 'email', icon: <Email /> });
    if (user.verificationStatus.phone) badges.push({ type: 'phone', icon: <Phone /> });
    if (user.verificationStatus.identity) badges.push({ type: 'identity', icon: <VerifiedUser /> });
    if (user.verificationStatus.address) badges.push({ type: 'address', icon: <LocationOn /> });
    return badges;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ color: '#a5816d', mb: 3 }}>
        <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
        Gestion des utilisateurs
      </Typography>

      {/* Filtres et recherche */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Rechercher un utilisateur..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ minWidth: 300 }}
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Filtrer</InputLabel>
          <Select
            value={filter}
            label="Filtrer"
            onChange={(e) => setFilter(e.target.value as any)}
          >
            <MenuItem value="all">Tous les utilisateurs</MenuItem>
            <MenuItem value="high_risk">Risque élevé</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="blocked">Bloqués</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadUsers}
        >
          Actualiser
        </Button>
      </Box>

      {/* Statistiques rapides */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {users.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total utilisateurs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error">
                {users.filter(u => u.riskScore >= 70).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Risque élevé
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning">
                {users.filter(u => u.status === 'pending_verification').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                En attente
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success">
                {users.filter(u => u.isVerified).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vérifiés
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tableau des utilisateurs */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Vérification</TableCell>
              <TableCell>Score de risque</TableCell>
              <TableCell>Activité suspecte</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => {
              const riskLevel = getRiskLevel(user.riskScore);
              const verificationBadges = getVerificationBadges(user);
              
              return (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ position: 'relative', mr: 2 }}>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            user.suspiciousActivity.length > 0 ? (
                              <Flag color="error" sx={{ fontSize: 16 }} />
                            ) : null
                          }
                        >
                          <Avatar src={user.avatar} />
                        </Badge>
                        <VerifiedBadge 
                          isVerified={user.isVerified} 
                          position="avatar" 
                          size="small"
                        />
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2">{user.name}</Typography>
                          <VerifiedBadge 
                            isVerified={user.isVerified} 
                            position="inline" 
                            size="small"
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {user.location} • {user.age} ans
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.status} 
                      color={getStatusColor(user.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {verificationBadges.map((badge, index) => (
                        <Tooltip key={index} title={badge.type}>
                          {badge.icon}
                        </Tooltip>
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {user.profileCompleteness}% complet
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={user.riskScore} 
                        sx={{ width: 60, mr: 1 }}
                        color={riskLevel.color as any}
                      />
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                          {user.riskScore}%
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: `${riskLevel.color}.main` }}>
                          {riskLevel.level}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.suspiciousActivity.length > 0 ? (
                      <Box>
                        <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                          {user.suspiciousActivity.length} alerte(s)
                        </Typography>
                        {user.suspiciousActivity.slice(0, 2).map((activity, index) => (
                          <Chip
                            key={index}
                            label={activity.type}
                            color={getSeverityColor(activity.severity) as any}
                            size="small"
                            sx={{ mr: 0.5, mt: 0.5 }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="success">
                        Aucune alerte
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Voir détails">
                        <IconButton size="small" onClick={() => {
                          setSelectedUser(user);
                          setOpenDetailsDialog(true);
                        }}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {user.status === 'active' ? (
                        <Tooltip title="Bloquer">
                          <IconButton size="small" onClick={() => handleUserAction(user.id, 'block')}>
                            <Block color="error" />
                          </IconButton>
                        </Tooltip>
                      ) : user.status === 'blocked' ? (
                        <Tooltip title="Débloquer">
                          <IconButton size="small" onClick={() => handleUserAction(user.id, 'unblock')}>
                            <CheckCircle color="success" />
                          </IconButton>
                        </Tooltip>
                      ) : user.status === 'pending_verification' ? (
                        <Tooltip title="Vérifier">
                          <IconButton size="small" onClick={() => handleUserAction(user.id, 'verify')}>
                            <VerifiedUser color="success" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de détails utilisateur */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ position: 'relative', mr: 2 }}>
              <Avatar src={selectedUser?.avatar} />
              <VerifiedBadge 
                isVerified={selectedUser?.isVerified || false} 
                position="avatar" 
                size="medium"
              />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">{selectedUser?.name}</Typography>
                <VerifiedBadge 
                  isVerified={selectedUser?.isVerified || false} 
                  position="inline" 
                  size="medium"
                  showText={true}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {selectedUser?.email}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Informations personnelles
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Âge" 
                        secondary={selectedUser.age}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Localisation" 
                        secondary={selectedUser.location}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Téléphone" 
                        secondary={selectedUser.phone}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Date d'inscription" 
                        secondary={selectedUser.registrationDate.toLocaleDateString('fr-FR')}
                      />
                    </ListItem>
                  </List>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Activité sur la plateforme
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Transactions" 
                        secondary={`${selectedUser.totalTransactions} (${selectedUser.totalSpent}€)`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Note moyenne" 
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Star sx={{ color: '#ffc107', mr: 0.5 }} />
                            {selectedUser.rating}
                          </Box>
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Signalements" 
                        secondary={selectedUser.reports}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Dernière activité" 
                        secondary={selectedUser.lastActivity.toLocaleString('fr-FR')}
                      />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Score de risque et vérifications
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Score de risque: {selectedUser.riskScore}% ({getRiskLevel(selectedUser.riskScore).level})
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={selectedUser.riskScore} 
                      color={getRiskLevel(selectedUser.riskScore).color as any}
                      sx={{ mb: 2 }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Complétude du profil: {selectedUser.profileCompleteness}%
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={selectedUser.profileCompleteness} 
                      color="primary"
                    />
                  </Box>
                </Grid>

                {selectedUser.suspiciousActivity.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom color="error">
                      Activité suspecte
                    </Typography>
                    <List>
                      {selectedUser.suspiciousActivity.map((activity, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Warning color={getSeverityColor(activity.severity) as any} />
                          </ListItemIcon>
                          <ListItemText
                            primary={activity.description}
                            secondary={`${activity.type} • ${activity.timestamp.toLocaleString('fr-FR')}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                )}

                {selectedUser.bio && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Bio
                    </Typography>
                    <Typography variant="body2">
                      {selectedUser.bio}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Fermer</Button>
          {selectedUser && (
            <>
              {selectedUser.status === 'active' ? (
                <Button 
                  color="error" 
                  onClick={() => {
                    handleUserAction(selectedUser.id, 'block');
                    setOpenDetailsDialog(false);
                  }}
                >
                  Bloquer
                </Button>
              ) : selectedUser.status === 'blocked' ? (
                <Button 
                  color="success" 
                  onClick={() => {
                    handleUserAction(selectedUser.id, 'unblock');
                    setOpenDetailsDialog(false);
                  }}
                >
                  Débloquer
                </Button>
              ) : selectedUser.status === 'pending_verification' ? (
                <Button 
                  color="primary" 
                  onClick={() => {
                    handleUserAction(selectedUser.id, 'verify');
                    setOpenDetailsDialog(false);
                  }}
                >
                  Vérifier
                </Button>
              ) : null}
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
